import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Separator } from '../components/ui/separator';
import { 
  FileText, Download, Filter, Building2, Package, Users, 
  BarChart2, MapPin, Calendar, Info, Loader2, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper for API calls
const apiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...options.headers
  };
  
  const response = await fetch(`${API_URL}/api${endpoint}`, { ...options, headers });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Erro' }));
    throw error;
  }
  return response;
};

const REPORT_TYPES = [
  {
    id: 'rede',
    name: 'Relatório da Rede',
    description: 'Estatísticas gerais da rede ENTRAJUDA',
    icon: Building2,
    color: 'bg-blue-500',
    filters: []
  },
  {
    id: 'necessidades',
    name: 'Relatório de Necessidades',
    description: 'Necessidades por tipo de recurso, demografia e região',
    icon: Package,
    color: 'bg-amber-500',
    filters: ['status', 'tipo_recurso', 'distrito']
  },
  {
    id: 'stock',
    name: 'Relatório de Stock',
    description: 'Inventário atual com atributos demográficos',
    icon: BarChart2,
    color: 'bg-green-500',
    filters: ['tipo_recurso', 'armazem']
  }
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos os status' },
  { value: 'Pendente', label: 'Pendente' },
  { value: 'Em Análise', label: 'Em Análise' },
  { value: 'Alocada', label: 'Alocada' },
  { value: 'Concluída', label: 'Concluída' }
];

const ARMAZEM_OPTIONS = [
  { value: 'all', label: 'Todos os armazéns' },
  { value: 'Lisboa', label: 'Lisboa' },
  { value: 'Porto', label: 'Porto' },
  { value: 'Coimbra', label: 'Coimbra' },
  { value: 'Faro', label: 'Faro' }
];

export default function RelatoriosPage() {
  const [loading, setLoading] = useState({});
  const [tiposRecursos, setTiposRecursos] = useState({});
  const [distritos, setDistritos] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    tipo_recurso: '',
    distrito_id: '',
    armazem: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [tiposRes, distritosRes, dashRes] = await Promise.all([
        apiCall('/tipos-recursos').then(r => r.json()),
        apiCall('/distritos').then(r => r.json()),
        apiCall('/recursos/dashboard').then(r => r.json())
      ]);
      setTiposRecursos(tiposRes || {});
      setDistritos(distritosRes || []);
      setStats(dashRes);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleDownload = async (reportType) => {
    setLoading(prev => ({ ...prev, [reportType]: true }));
    
    try {
      let endpoint = `/relatorios/${reportType}/pdf`;
      const params = new URLSearchParams();
      
      if (reportType === 'necessidades') {
        if (filters.status && filters.status !== 'all') params.append('status', filters.status);
        if (filters.tipo_recurso && filters.tipo_recurso !== 'all') params.append('tipo_recurso', filters.tipo_recurso);
        if (filters.distrito_id && filters.distrito_id !== 'all') params.append('distrito_id', filters.distrito_id);
      } else if (reportType === 'stock') {
        if (filters.tipo_recurso && filters.tipo_recurso !== 'all') params.append('tipo_recurso', filters.tipo_recurso);
        if (filters.armazem && filters.armazem !== 'all') params.append('armazem', filters.armazem);
      }
      
      if (params.toString()) {
        endpoint += `?${params.toString()}`;
      }
      
      const response = await apiCall(endpoint);
      const blob = await response.blob();
      
      // Download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `relatorio_${reportType}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Relatório gerado com sucesso!');
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error(error.detail || 'Erro ao gerar relatório');
    } finally {
      setLoading(prev => ({ ...prev, [reportType]: false }));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Relatórios PDF</h1>
        <p className="text-slate-500 mt-1">
          Gere relatórios detalhados com estatísticas por tipo de recurso, demografia e região
        </p>
      </div>

      {/* Info Alert */}
      <Alert className="bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-700">
          Os relatórios são gerados em formato PDF e incluem estatísticas agregadas por tipo de recurso, 
          sexo, faixa etária e distribuição geográfica.
        </AlertDescription>
      </Alert>

      {/* Quick Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{stats.total_disponivel?.toLocaleString('pt-PT') || 0}</p>
                <p className="text-sm text-slate-500">Stock Disponível</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-amber-600">{stats.necessidades_pendentes || 0}</p>
                <p className="text-sm text-slate-500">Necessidades Pendentes</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{stats.necessidades_alocadas || 0}</p>
                <p className="text-sm text-slate-500">Necessidades Alocadas</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-slate-700">{stats.total_itens_stock || 0}</p>
                <p className="text-sm text-slate-500">Itens em Stock</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Report Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {REPORT_TYPES.map((report) => {
          const Icon = report.icon;
          const isLoading = loading[report.id];
          
          return (
            <Card key={report.id} className="relative overflow-hidden">
              <div className={`absolute top-0 left-0 right-0 h-1 ${report.color}`} />
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${report.color} text-white`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{report.name}</CardTitle>
                      <CardDescription className="mt-1">{report.description}</CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Filters for this report type */}
                {report.filters.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <Filter className="w-4 h-4" />
                      Filtros
                    </div>
                    
                    {report.filters.includes('status') && (
                      <Select
                        value={filters.status}
                        onValueChange={(v) => setFilters(prev => ({ ...prev, status: v }))}
                      >
                        <SelectTrigger className="text-sm">
                          <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    
                    {report.filters.includes('tipo_recurso') && (
                      <Select
                        value={filters.tipo_recurso}
                        onValueChange={(v) => setFilters(prev => ({ ...prev, tipo_recurso: v }))}
                      >
                        <SelectTrigger className="text-sm">
                          <SelectValue placeholder="Tipo de Recurso" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos os tipos</SelectItem>
                          {Object.entries(tiposRecursos).map(([key, config]) => (
                            <SelectItem key={key} value={key}>{config.nome}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    
                    {report.filters.includes('distrito') && (
                      <Select
                        value={filters.distrito_id}
                        onValueChange={(v) => setFilters(prev => ({ ...prev, distrito_id: v }))}
                      >
                        <SelectTrigger className="text-sm">
                          <SelectValue placeholder="Distrito" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos os distritos</SelectItem>
                          {distritos.map(d => (
                            <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    
                    {report.filters.includes('armazem') && (
                      <Select
                        value={filters.armazem}
                        onValueChange={(v) => setFilters(prev => ({ ...prev, armazem: v }))}
                      >
                        <SelectTrigger className="text-sm">
                          <SelectValue placeholder="Armazém" />
                        </SelectTrigger>
                        <SelectContent>
                          {ARMAZEM_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}
                
                <Separator />
                
                <Button 
                  onClick={() => handleDownload(report.id)}
                  disabled={isLoading}
                  className="w-full"
                  data-testid={`download-${report.id}-btn`}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      A gerar...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Descarregar PDF
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Report Contents Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Conteúdo dos Relatórios</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-blue-500" />
                Relatório da Rede
              </h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Total de instituições ativas</li>
                <li>• Total de voluntários e missões</li>
                <li>• Distribuição por distrito</li>
                <li>• KPIs gerais da rede</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <Package className="w-4 h-4 text-amber-500" />
                Relatório de Necessidades
              </h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Resumo por status</li>
                <li>• Distribuição por tipo de recurso</li>
                <li>• Análise por sexo e faixa etária</li>
                <li>• Distribuição geográfica por distrito</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <BarChart2 className="w-4 h-4 text-green-500" />
                Relatório de Stock
              </h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Quantidades por tipo de recurso</li>
                <li>• Distribuição por sexo</li>
                <li>• Distribuição por faixa etária</li>
                <li>• Distribuição por armazém</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
